import { toaster } from 'evergreen-ui';
import Request from '../../../util/Api';
import ModuleConfig from '../Config';

const actions = ModuleConfig.Actions;
const endpoints = ModuleConfig.Endpoints;

// Debug: Log available actions
console.log('Available actions:', Object.keys(actions));
console.log('Has employeeApproveChange:', !!actions.employeeApproveChange);
console.log('Has employeeRejectChange:', !!actions.employeeRejectChange);

const EmployeeSchedule = ModuleConfig.Structures.EmployeeSchedule;
const Shop = ModuleConfig.Structures.Shop;
const ShopEmployee = ModuleConfig.Structures.ShopEmployee;
const VacationRequest = ModuleConfig.Structures.VacationRequest;
const ScheduleChangeRequest = ModuleConfig.Structures.ScheduleChangeRequest;

// ============ SCHEDULES ============

export function getSchedules(opts = {}) {
    const { startDate, endDate, shopId, employeeId } = opts;

    return async (dispatch) => {
        dispatch({ type: actions.getSchedules.request.id });

        try {
            const result = await Request.get(endpoints.schedulesByDateRange, null, {
                startDate,
                endDate,
                shopId,
                employeeId
            });

            const data = result.data.map(item => new EmployeeSchedule(item));
            dispatch({ type: actions.getSchedules.success.id, options: { data } });
        } catch (err) {
            console.error(err);
            dispatch({ type: actions.getSchedules.failure.id, options: err });
            toaster.danger("Programul nu a putut fi încărcat");
        }
    };
}

export function createSchedule(values) {
    return async (dispatch) => {
        dispatch({ type: actions.createSchedule.request.id });

        try {
            const result = await Request.post(endpoints.schedules, null, {}, values);
            const schedule = new EmployeeSchedule(result.data);

            dispatch({ type: actions.createSchedule.success.id, options: schedule });
            toaster.success("Tura a fost creată");
            return schedule;
        } catch (err) {
            console.error(err);
            dispatch({ type: actions.createSchedule.failure.id, options: err });
            toaster.danger(err.message || "Tura nu a putut fi creată");
            throw err;
        }
    };
}

export function updateSchedule(id, values) {
    return async (dispatch) => {
        dispatch({ type: actions.updateSchedule.request.id });

        try {
            const result = await Request.put(endpoints.schedule, { id }, {}, values);

            // Check if a change request was created instead of direct update
            if (result.changeRequestCreated) {
                const changeRequest = new ScheduleChangeRequest(result.data);
                dispatch({ type: actions.createSwapRequest.success.id, options: changeRequest });
                toaster.notify(result.message || "Cerere de modificare creată. Așteaptă aprobare.");
                return { changeRequestCreated: true, data: changeRequest };
            }

            const schedule = new EmployeeSchedule(result.data);

            dispatch({ type: actions.updateSchedule.success.id, options: schedule });
            toaster.success("Tura a fost actualizată");
            return { changeRequestCreated: false, data: schedule };
        } catch (err) {
            console.error(err);
            dispatch({ type: actions.updateSchedule.failure.id, options: err });
            toaster.danger("Tura nu a putut fi actualizată");
            throw err;
        }
    };
}

export function deleteSchedule(id) {
    return async (dispatch) => {
        dispatch({ type: actions.deleteSchedule.request.id });

        try {
            await Request.delete(endpoints.schedule, { id });

            dispatch({ type: actions.deleteSchedule.success.id, options: { id } });
            toaster.success("Tura a fost ștearsă");
        } catch (err) {
            console.error(err);
            dispatch({ type: actions.deleteSchedule.failure.id, options: err });
            toaster.danger("Tura nu a putut fi ștearsă");
        }
    };
}

export function bulkCreateSchedules(schedules) {
    return async (dispatch) => {
        dispatch({ type: actions.bulkCreateSchedules.request.id });

        try {
            const result = await Request.post(endpoints.bulkSchedules, null, {}, { schedules });
            const data = result.data.map(item => new EmployeeSchedule(item));

            dispatch({ type: actions.bulkCreateSchedules.success.id, options: { data } });

            if (result.errors && result.errors.length > 0) {
                toaster.warning(`${result.created} ture create, ${result.failed} erori`);
            } else {
                toaster.success(`${result.created} ture create`);
            }

            return result;
        } catch (err) {
            console.error(err);
            dispatch({ type: actions.bulkCreateSchedules.failure.id, options: err });
            toaster.danger("Turele nu au putut fi create");
            throw err;
        }
    };
}

// ============ STATS & REPORTS ============

export function getWeeklyStats(weekStart, shopId = null) {
    return async (dispatch) => {
        dispatch({ type: actions.getWeeklyStats.request.id });

        try {
            const result = await Request.get(endpoints.weeklyStats, null, { weekStart, shopId });

            dispatch({ type: actions.getWeeklyStats.success.id, options: result.data });
        } catch (err) {
            console.error(err);
            dispatch({ type: actions.getWeeklyStats.failure.id, options: err });
            toaster.danger("Statisticile nu au putut fi încărcate");
        }
    };
}

export function getCoverageReport(date, shopId) {
    return async (dispatch) => {
        dispatch({ type: actions.getCoverageReport.request.id });

        try {
            const result = await Request.get(endpoints.coverageReport, null, { date, shopId });

            dispatch({ type: actions.getCoverageReport.success.id, options: result.data });
        } catch (err) {
            console.error(err);
            dispatch({ type: actions.getCoverageReport.failure.id, options: err });
            toaster.danger("Raportul de acoperire nu a putut fi încărcat");
        }
    };
}

// ============ SHOPS & EMPLOYEES ============

export function getShops() {
    return async (dispatch) => {
        dispatch({ type: actions.getShops.request.id });

        try {
            const result = await Request.get(endpoints.shops, null, { _start: 0, _end: 100 });
            const data = result.data.map(item => new Shop(item));

            dispatch({ type: actions.getShops.success.id, options: data });
        } catch (err) {
            console.error(err);
            dispatch({ type: actions.getShops.failure.id, options: err });
        }
    };
}

export function getShopEmployees(shopId) {
    return async (dispatch) => {
        dispatch({ type: actions.getEmployees.request.id });

        try {
            const result = await Request.get(endpoints.shopEmployees, { id: shopId });
            const data = result.data.map(item => new ShopEmployee(item));

            dispatch({ type: actions.getEmployees.success.id, options: data });
        } catch (err) {
            console.error(err);
            dispatch({ type: actions.getEmployees.failure.id, options: err });
        }
    };
}

// ============ VACATION REQUESTS ============

export function getVacationRequests(opts = {}) {
    return async (dispatch) => {
        dispatch({ type: actions.getVacationRequests.request.id });

        try {
            const result = await Request.get(endpoints.vacationRequests, null, {
                _start: opts.start || 0,
                _end: (opts.start || 0) + (opts.limit || 100),
                extraFilters: JSON.stringify(opts.extraFilters || {})
            });
            const data = result.data.map(item => new VacationRequest(item));

            dispatch({ type: actions.getVacationRequests.success.id, options: data });
        } catch (err) {
            console.error(err);
            dispatch({ type: actions.getVacationRequests.failure.id, options: err });
            toaster.danger("Cererile de concediu nu au putut fi încărcate");
        }
    };
}

export function createVacationRequest(values) {
    return async (dispatch) => {
        dispatch({ type: actions.createVacationRequest.request.id });

        try {
            const result = await Request.post(endpoints.vacationRequests, null, {}, values);
            const request = new VacationRequest(result.data);

            dispatch({ type: actions.createVacationRequest.success.id, options: request });
            toaster.success("Cererea de concediu a fost trimisă");
            return request;
        } catch (err) {
            console.error(err);
            dispatch({ type: actions.createVacationRequest.failure.id, options: err });
            toaster.danger(err.message || "Cererea nu a putut fi trimisă");
            throw err;
        }
    };
}

export function approveVacationRequest(id, adminNotes = '') {
    return async (dispatch) => {
        dispatch({ type: actions.approveVacationRequest.request.id });

        try {
            const result = await Request.post(endpoints.approveVacation, { id }, {}, { adminNotes });
            const request = new VacationRequest(result.data);

            dispatch({ type: actions.approveVacationRequest.success.id, options: request });
            toaster.success("Cererea a fost aprobată");
            return request;
        } catch (err) {
            console.error(err);
            dispatch({ type: actions.approveVacationRequest.failure.id, options: err });
            toaster.danger("Cererea nu a putut fi aprobată");
            throw err;
        }
    };
}

export function rejectVacationRequest(id, adminNotes = '') {
    return async (dispatch) => {
        dispatch({ type: actions.rejectVacationRequest.request.id });

        try {
            const result = await Request.post(endpoints.rejectVacation, { id }, {}, { adminNotes });
            const request = new VacationRequest(result.data);

            dispatch({ type: actions.rejectVacationRequest.success.id, options: request });
            toaster.success("Cererea a fost respinsă");
            return request;
        } catch (err) {
            console.error(err);
            dispatch({ type: actions.rejectVacationRequest.failure.id, options: err });
            toaster.danger("Cererea nu a putut fi respinsă");
            throw err;
        }
    };
}

export function cancelVacationRequest(id) {
    return async (dispatch) => {
        dispatch({ type: actions.cancelVacationRequest.request.id });

        try {
            const result = await Request.post(endpoints.cancelVacation, { id });
            const request = new VacationRequest(result.data);

            dispatch({ type: actions.cancelVacationRequest.success.id, options: request });
            toaster.success("Concediul a fost anulat");
            return request;
        } catch (err) {
            console.error(err);
            dispatch({ type: actions.cancelVacationRequest.failure.id, options: err });
            toaster.danger("Concediul nu a putut fi anulat");
            throw err;
        }
    };
}

// ============ CHANGE REQUESTS ============

export function getChangeRequests(opts = {}) {
    return async (dispatch) => {
        dispatch({ type: actions.getChangeRequests.request.id });

        try {
            const endpoint = opts.pendingOnly ? endpoints.pendingChangeRequests : endpoints.changeRequests;
            const result = await Request.get(endpoint, null, {
                _start: opts.start || 0,
                _end: (opts.start || 0) + (opts.limit || 100)
            });
            const data = result.data.map(item => new ScheduleChangeRequest(item));

            dispatch({ type: actions.getChangeRequests.success.id, options: data });
        } catch (err) {
            console.error(err);
            dispatch({ type: actions.getChangeRequests.failure.id, options: err });
            toaster.danger("Cererile de schimb nu au putut fi încărcate");
        }
    };
}

export function createSwapRequest(originalScheduleId, swapWithScheduleId, reason = '') {
    return async (dispatch) => {
        dispatch({ type: actions.createSwapRequest.request.id });

        try {
            const result = await Request.post(endpoints.createSwap, null, {}, {
                originalScheduleId,
                swapWithScheduleId,
                reason
            });
            const request = new ScheduleChangeRequest(result.data);

            dispatch({ type: actions.createSwapRequest.success.id, options: request });
            toaster.success("Cererea de schimb a fost trimisă");
            return request;
        } catch (err) {
            console.error(err);
            dispatch({ type: actions.createSwapRequest.failure.id, options: err });
            toaster.danger(err.message || "Cererea nu a putut fi trimisă");
            throw err;
        }
    };
}

export function approveChangeRequest(id, adminNotes = '') {
    return async (dispatch) => {
        dispatch({ type: actions.approveChangeRequest.request.id });

        try {
            const result = await Request.post(endpoints.approveChange, { id }, {}, { adminNotes });
            const request = new ScheduleChangeRequest(result.data);

            dispatch({ type: actions.approveChangeRequest.success.id, options: request });
            toaster.success("Cererea de schimb a fost aprobată");
            return request;
        } catch (err) {
            console.error(err);
            dispatch({ type: actions.approveChangeRequest.failure.id, options: err });
            toaster.danger("Cererea nu a putut fi aprobată");
            throw err;
        }
    };
}

export function rejectChangeRequest(id, adminNotes = '') {
    return async (dispatch) => {
        dispatch({ type: actions.rejectChangeRequest.request.id });

        try {
            const result = await Request.post(endpoints.rejectChange, { id }, {}, { adminNotes });
            const request = new ScheduleChangeRequest(result.data);

            dispatch({ type: actions.rejectChangeRequest.success.id, options: request });
            toaster.success("Cererea de schimb a fost respinsă");
            return request;
        } catch (err) {
            console.error(err);
            dispatch({ type: actions.rejectChangeRequest.failure.id, options: err });
            toaster.danger("Cererea nu a putut fi respinsă");
            throw err;
        }
    };
}

export function overrideChangeRequest(id, adminNotes = '') {
    return async (dispatch) => {
        dispatch({ type: actions.overrideChangeRequest.request.id });

        try {
            const result = await Request.post(endpoints.overrideChange, { id }, {}, {
                adminNotes,
                confirmOverride: true
            });
            const request = new ScheduleChangeRequest(result.data);

            dispatch({ type: actions.overrideChangeRequest.success.id, options: request });
            toaster.success("Cererea de schimb a fost aprobată (override admin)");
            return request;
        } catch (err) {
            console.error(err);
            dispatch({ type: actions.overrideChangeRequest.failure.id, options: err });
            toaster.danger("Cererea nu a putut fi aprobată");
            throw err;
        }
    };
}

export function employeeApproveChange(id) {
    return async (dispatch) => {
        if (!actions.employeeApproveChange) {
            console.error('employeeApproveChange action not found - please restart the dev server');
            toaster.danger('Eroare: Acțiunea nu este disponibilă. Reîmprospătează pagina.');
            throw new Error('Action not available');
        }

        dispatch({ type: actions.employeeApproveChange.request.id });

        try {
            const result = await Request.post(endpoints.employeeApproveChange, { id });
            const request = new ScheduleChangeRequest(result.data);

            dispatch({ type: actions.employeeApproveChange.success.id, options: request });
            toaster.success("Ai acceptat schimbul. Cererea a fost trimisă către management.");
            return request;
        } catch (err) {
            console.error(err);
            dispatch({ type: actions.employeeApproveChange.failure.id, options: err });
            toaster.danger(err.message || "Cererea nu a putut fi aprobată");
            throw err;
        }
    };
}

export function employeeRejectChange(id) {
    return async (dispatch) => {
        if (!actions.employeeRejectChange) {
            console.error('employeeRejectChange action not found - please restart the dev server');
            toaster.danger('Eroare: Acțiunea nu este disponibilă. Reîmprospătează pagina.');
            throw new Error('Action not available');
        }

        dispatch({ type: actions.employeeRejectChange.request.id });

        try {
            const result = await Request.post(endpoints.employeeRejectChange, { id });
            const request = new ScheduleChangeRequest(result.data);

            dispatch({ type: actions.employeeRejectChange.success.id, options: request });
            toaster.success("Ai respins cererea de schimb.");
            return request;
        } catch (err) {
            console.error(err);
            dispatch({ type: actions.employeeRejectChange.failure.id, options: err });
            toaster.danger(err.message || "Cererea nu a putut fi respinsă");
            throw err;
        }
    };
}
